export function resolveMemberId(event: any): any {
  if (typeof event === 'string') {
    return null;
  }
  return event?.memberId ?? event?.id ?? event?.member?.id;
}

export function resolveSubscriberEnrollmentId(event: any): any {
  if (typeof event === 'string') {
    return null;
  }
  return event?.subscriberEnrollmentId
    ?? event?.subscriberEnrollmentID
    ?? event?.enrollmentId
    ?? event?.enrollment?.subscriberEnrollmentId
    ?? event?.enrollment?.subscriberEnrollmentID
    ?? event?.enrollment?.enrollmentId
    ?? event?.subscriber?.subscriberEnrollmentId
    ?? event?.subscriber?.subscriberEnrollmentID
    ?? event?.subscriber?.enrollmentId
    ?? event?.dependent?.subscriberEnrollmentId
    ?? event?.dependent?.subscriberEnrollmentID
    ?? event?.dependent?.enrollmentId;
}

export function buildSelectedSubscriber(event: any, subscriberEnrollmentId: any): any {
  const normalizedSubscriberEnrollmentId = subscriberEnrollmentId ?? resolveSubscriberEnrollmentId(event);

  return {
    ...event,
    firstName: event?.name?.first ?? event?.firstName,
    lastName: event?.name?.last ?? event?.lastName,
    planAcronym: resolvePlanAcronym(event),
    clientSpecifiedIdentifier: resolveClientSpecifiedIdentifier(event),
    subclientSpecifiedIdentifier: resolveSubClientSpecifiedIdentifier(event),
    asOf: resolveAsOf(event),
    subscriberEnrollmentId: normalizedSubscriberEnrollmentId,
    subscriberEnrollmentID: normalizedSubscriberEnrollmentId
  };
}

function resolvePlanAcronym(event: any): any {
  return event?.planAcronym
    ?? event?.plan
    ?? event?.enrollment?.plan
    ?? event?.client?.planAcronym;
}

function resolveClientSpecifiedIdentifier(event: any): any {
  return event?.clientSpecifiedIdentifier
    ?? event?.clientSpecifiedId
    ?? event?.clientId
    ?? event?.client?.specifiedId
    ?? event?.enrollment?.clientSpecifiedIdentifier
    ?? event?.enrollment?.clientSpecifiedId;
}

function resolveSubClientSpecifiedIdentifier(event: any): any {
  return event?.subclientSpecifiedIdentifier
    ?? event?.subClientSpecifiedIdentifier
    ?? event?.subclientSpecifiedId
    ?? event?.subClientSpecifiedId
    ?? event?.subClientId
    ?? event?.specifiedId
    ?? event?.subClient?.specifiedId
    ?? event?.enrollment?.subClientSpecifiedIdentifier
    ?? event?.enrollment?.subclientSpecifiedIdentifier
    ?? event?.enrollment?.subClientSpecifiedId;
}

function resolveAsOf(event: any): any {
  return event?.asOf
    ?? event?.asOfDate
    ?? event?.enrollment?.asOf
    ?? event?.enrollment?.asOfDate
    ?? event?.searchCriteria?.asOfDate
    ?? '';
}

export function resolveSubscriberName(subscriber: any): string {
  return [subscriber?.name?.first ?? subscriber?.firstName, subscriber?.name?.last ?? subscriber?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
}

export type AddFlowHandoffEvent = {
  event: 'add flow';
  foundMember: any;
};

export function isAddFlowHandoffEvent(event: any): event is AddFlowHandoffEvent | 'add flow' {
  return event === 'add flow'
    || (!!event && typeof event === 'object' && event.event === 'add flow');
}

export function resolveAddFlowHandoffFoundMember(event: any): any {
  if (!event || typeof event !== 'object' || event.event !== 'add flow') {
    return null;
  }

  return event.foundMember ?? null;
}

export function normalizeCountryCode(country: any): string {
  const value = extractCodeValue(country);
  if (!value) {
    return 'US';
  }

  const normalized = value.replace(/\./g, '').trim().toUpperCase();
  if (normalized === 'UNITED STATES' || normalized === 'UNITED STATES OF AMERICA' || normalized === 'USA' || normalized === 'US') {
    return 'US';
  }
  if (normalized === 'CANADA' || normalized === 'CA') {
    return 'CA';
  }

  return value.length === 2 ? value.toUpperCase() : value;
}

export function normalizeSubdivisionCode(subdivision: any): string {
  const value = extractCodeValue(subdivision);
  if (!value) {
    return '';
  }

  return value.length === 2 ? value.toUpperCase() : value;
}

export function normalizeFoundMemberForPrefill(member: any): any {
  if (!hasFoundMemberPrefillData(member)) {
    return null;
  }

  const name = member?.name || {};
  const normalizedBirthDate = normalizeIsoDate(member?.birthDate ?? member?.dob ?? member?.dateOfBirth);
  const normalizedName = {
    prefix: normalizeTextValue(name?.prefix ?? member?.prefix),
    first: normalizeTextValue(name?.first ?? member?.firstName),
    middle: normalizeTextValue(name?.middle ?? member?.middleName),
    last: normalizeTextValue(name?.last ?? member?.lastName),
    suffix: normalizeTextValue(name?.suffix ?? member?.suffix)
  };

  const normalizedContacts = Array.isArray(member?.contacts)
    ? member.contacts.map((contact: any) => normalizeContactForPrefill(contact))
    : [];

  const hasGeneralContact = normalizedContacts.some((contact: any) => {
    const code = `${contact?.personContactType?.code ?? ''}`.trim().toUpperCase();
    return !code || code === 'GENERAL';
  });

  const contacts = !hasGeneralContact && normalizedContacts.length > 0
    ? [{
        ...normalizedContacts[0],
        personContactType: { code: 'GENERAL', value: 'GENERAL' }
      }, ...normalizedContacts]
    : normalizedContacts;

  return {
    ...member,
    name: normalizedName,
    firstName: normalizedName.first,
    middleName: normalizedName.middle,
    lastName: normalizedName.last,
    birthDate: normalizedBirthDate,
    dob: normalizedBirthDate,
    dateOfBirth: normalizedBirthDate,
    contacts
  };
}

export function hasFoundMemberPrefillData(member: any): boolean {
  if (!member || typeof member === 'string') {
    return false;
  }

  return [
    resolveMemberId(member),
    resolveSubscriberEnrollmentId(member),
    member?.name?.first,
    member?.name?.last,
    member?.birthDate,
    member?.enrollment?.plan,
    member?.enrollment?.clientSpecifiedIdentifier,
    member?.enrollment?.subClientSpecifiedIdentifier
  ].some(value => value !== null && value !== undefined && `${value}`.trim() !== '');
}

function normalizeContactForPrefill(contact: any): any {
  const line1 = normalizeTextValue(contact?.address?.line1 ?? contact?.address?.lines?.[0]);
  const line2 = normalizeTextValue(contact?.address?.line2 ?? contact?.address?.lines?.[1]);

  return {
    ...contact,
    name: contact?.name ?? {},
    emails: normalizeEmailList(contact?.emails),
    phones: {
      primary: normalizeTextValue(contact?.phones?.primary),
      secondary: normalizeTextValue(contact?.phones?.secondary),
      cell: normalizeTextValue(contact?.phones?.cell),
      phoneExtension: normalizeTextValue(contact?.phones?.phoneExtension)
    },
    faxNumber: normalizeTextValue(contact?.faxNumber),
    personContactType: {
      code: normalizeTextValue(contact?.personContactType?.code),
      value: normalizeTextValue(contact?.personContactType?.value)
    },
    address: {
      ...(contact?.address || {}),
      zip: normalizeTextValue(contact?.address?.zip),
      country: normalizeCountryCode(contact?.address?.country),
      city: normalizeTextValue(contact?.address?.city),
      county: normalizeTextValue(contact?.address?.county),
      state: normalizeSubdivisionCode(contact?.address?.state),
      lines: normalizeAddressLines(contact?.address?.lines, line1, line2),
      line1,
      line2
    }
  };
}

function normalizeAddressLines(lines: any, line1: string, line2: string): string[] {
  if (Array.isArray(lines) && lines.length > 0) {
    return lines.map(line => normalizeTextValue(line));
  }

  return [line1, line2].filter(Boolean);
}

function normalizeEmailList(emails: any): string[] {
  if (!Array.isArray(emails)) {
    return [];
  }

  return emails
    .map(email => normalizeTextValue(email))
    .filter(email => email !== '');
}

function extractCodeValue(value: any): string {
  const rawValue = typeof value === 'object'
    ? value?.code ?? value?.value ?? value?.label ?? value?.description ?? ''
    : value;

  return `${rawValue ?? ''}`.trim();
}

function normalizeTextValue(value: any): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return `${value}`.trim();
  }

  if (typeof value === 'object') {
    return extractCodeValue(value);
  }

  return '';
}

export function normalizeIsoDate(value: any): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10);
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsedNumberDate = new Date(value);
    return Number.isNaN(parsedNumberDate.getTime()) ? '' : parsedNumberDate.toISOString().slice(0, 10);
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return '';
    }

    const isoDateMatch = /^(\d{4}-\d{2}-\d{2})/.exec(trimmedValue);
    if (isoDateMatch?.[1]) {
      return isoDateMatch[1];
    }

    const parsedStringDate = new Date(trimmedValue);
    return Number.isNaN(parsedStringDate.getTime()) ? '' : parsedStringDate.toISOString().slice(0, 10);
  }

  return '';
}
